/**
 * VALLEYSTEALZ - Ultra-Performance Canvas Scrollytelling Engine
 * 143 Master Frames @ 24fps
 */

class ScrollyCanvasEngine {
  constructor(options = {}) {
    this.canvas = document.getElementById(options.canvasId || 'hero-canvas');
    if (!this.canvas) {
      console.error('Canvas element not found!');
      return;
    }

    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.container = document.querySelector(options.containerSelector || '.scrolly-container');

    this.totalFrames = options.totalFrames || 241;

    // Shopify-aware frame URL resolver
    let framePrefix = 'assets/frames_hq/frame_';
    let frameSuffix = '.webp';

    if (typeof window !== 'undefined' && window.SHOPIFY_FRAME_SAMPLE) {
      let sample = window.SHOPIFY_FRAME_SAMPLE;
      if (sample.startsWith('//')) {
        sample = window.location.protocol + sample;
      }
      const match = sample.match(/^(.*frame_)000001(\.webp.*)$/);
      if (match) {
        framePrefix = match[1];
        frameSuffix = match[2];
      } else {
        const parts = sample.split('frame_000001.webp');
        if (parts.length === 2) {
          framePrefix = parts[0] + 'frame_';
          frameSuffix = '.webp' + parts[1];
        }
      }
    } else if (typeof window !== 'undefined' && window.SHOPIFY_ASSET_BASE) {
      let base = window.SHOPIFY_ASSET_BASE;
      if (base.startsWith('//')) {
        base = window.location.protocol + base;
      }
      framePrefix = base + 'frame_';
      frameSuffix = '.webp';
    }

    this.framePrefix = (options.framePrefix && !options.framePrefix.startsWith('assets/')) 
      ? options.framePrefix 
      : framePrefix;
    this.frameSuffix = (options.frameSuffix && !options.frameSuffix.includes('hq2026')) 
      ? options.frameSuffix 
      : frameSuffix;

    this.images = new Array(this.totalFrames);
    this.loadedCount = 0;
    this.hasRenderedFirstFrame = false;

    this.currentFrame = 0;
    this.targetFrame = 0;
    this.lastRenderedFrame = -1;
    this.lastRenderedImage = null;

    // Narrative overlays
    this.overlays = {
      hero: document.getElementById('overlay-hero'),
      deconstruction: document.getElementById('overlay-deconstruction'),
      blueprint: document.getElementById('overlay-blueprint'),
      cyberpunk: document.getElementById('overlay-cyberpunk'),
      dropHud: document.getElementById('overlay-drop-hud')
    };

    // Bottom scroll elements
    this.bottomProgressFill = document.getElementById('bottom-progress-fill');
    this.bottomScrollWrapper = document.getElementById('bottom-scroll-wrapper');
    this.comingSoonHero = document.getElementById('coming-soon-hero');

    // Preloader
    this.preloader = document.getElementById('preloader');
    this.loaderBar = document.getElementById('loader-bar-fill');
    this.loaderPercent = document.getElementById('loader-percentage');

    this.isMobile = (typeof window !== 'undefined') && window.innerWidth <= 768;
    this.currentActiveOverlay = null;
    this.cachedContainerTop = 0;
    this.cachedContainerHeight = 0;
    this.cachedViewportHeight = 0;
    this.cachedMaxScroll = 0;

    this.init();
  }

  init() {
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize(), { passive: true });
    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.handleResize(), 100);
    }, { passive: true });

    // Scroll listeners (passive scroll and touch for instant mobile responsiveness)
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    window.addEventListener('touchmove', () => this.handleScroll(), { passive: true });

    this.preloadAllFrames();
    this.handleScroll();
    this.renderLoop();

    // Safety fallback: Dismiss preloader after max 800ms
    setTimeout(() => {
      this.dismissPreloader();
    }, 800);
  }

  updateLayoutMetrics() {
    if (!this.container) return;
    const viewport = this.canvas ? this.canvas.parentElement : null;
    const viewportHeight = viewport ? viewport.clientHeight : window.innerHeight;
    this.cachedContainerTop = this.container.offsetTop;
    this.cachedContainerHeight = this.container.offsetHeight;
    this.cachedViewportHeight = viewportHeight;
    this.cachedMaxScroll = this.cachedContainerHeight - this.cachedViewportHeight;
  }

  handleResize() {
    if (!this.canvas) return;
    this.isMobile = window.innerWidth <= 768;
    // On high-DPI mobile screens, clamping DPR to 1.25 maximizes fill-rate while preserving crispness
    const dpr = this.isMobile ? Math.min(window.devicePixelRatio || 1, 1.25) : Math.min(window.devicePixelRatio || 1, 2);
    const viewport = this.canvas.parentElement;
    const w = viewport ? viewport.clientWidth : window.innerWidth;
    const h = viewport ? viewport.clientHeight : window.innerHeight;

    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = this.isMobile ? 'low' : 'high';

    this.updateLayoutMetrics();

    this.lastRenderedFrame = -1;
    this.drawFrame(Math.round(this.currentFrame));
  }

  formatFrameNumber(num) {
    return String(num).padStart(6, '0');
  }

  preloadAllFrames() {
    const total = this.totalFrames;
    const loadFrame = (index, onLoaded) => {
      if (this.images[index] && this.images[index].complete) {
        if (onLoaded) onLoaded();
        return;
      }
      const frameNum = index + 1;
      const img = new Image();
      img.decoding = 'async';
      img.src = `${this.framePrefix}${this.formatFrameNumber(frameNum)}${this.frameSuffix}`;
      img.onload = () => {
        this.images[index] = img;
        this.loadedCount++;
        const pct = Math.round((this.loadedCount / total) * 100);
        if (this.loaderBar) this.loaderBar.style.width = `${pct}%`;
        if (this.loaderPercent) this.loaderPercent.textContent = `${pct}%`;

        if (this.loadedCount >= 2) {
          this.dismissPreloader();
        }
        if (onLoaded) onLoaded();
      };
      img.onerror = () => {
        this.loadedCount++;
        if (onLoaded) onLoaded();
      };
      this.images[index] = img;
    };

    // 1. Immediate priority: Frame 1
    const firstImg = new Image();
    firstImg.decoding = 'async';
    firstImg.src = `${this.framePrefix}${this.formatFrameNumber(1)}${this.frameSuffix}`;
    firstImg.onload = () => {
      this.images[0] = firstImg;
      this.loadedCount++;
      this.drawFrame(0);
      this.dismissPreloader();
      startPreloadPipeline();
    };
    firstImg.onerror = (err) => {
      console.warn('[Scroller] Primary frame 1 load failed, trying plain webp:', firstImg.src, err);
      const cleanImg = new Image();
      cleanImg.decoding = 'async';
      cleanImg.src = `${this.framePrefix}${this.formatFrameNumber(1)}.webp`;
      cleanImg.onload = () => {
        this.images[0] = cleanImg;
        this.loadedCount++;
        this.drawFrame(0);
        this.dismissPreloader();
        startPreloadPipeline();
      };
      cleanImg.onerror = () => {
        this.dismissPreloader();
        startPreloadPipeline();
      };
    };
    this.images[0] = firstImg;

    const startPreloadPipeline = () => {
      if (this.isMobile) {
        // Fast Mobile Pipeline: Load intro frames 2-10
        for (let i = 1; i < Math.min(10, total); i++) {
          loadFrame(i);
        }
        // Then load every 2nd frame across the whole sequence (120 keyframes)
        for (let i = 10; i < total; i += 2) {
          loadFrame(i);
        }
        // Background load remaining intermediate frames with concurrency 4
        const remaining = [];
        for (let i = 11; i < total; i += 2) {
          if (!this.images[i]) remaining.push(i);
        }
        let active = 0;
        const pump = () => {
          while (active < 4 && remaining.length > 0) {
            const idx = remaining.shift();
            active++;
            loadFrame(idx, () => {
              active--;
              pump();
            });
          }
        };
        setTimeout(pump, 150);
      } else {
        // Desktop Pipeline
        for (let i = 1; i < Math.min(16, total); i++) {
          loadFrame(i);
        }
        for (let i = 16; i < total; i += 4) {
          loadFrame(i);
        }
        const remaining = [];
        for (let i = 1; i < total; i++) {
          if (!this.images[i]) remaining.push(i);
        }
        let activeWorkers = 0;
        const pumpQueue = () => {
          while (activeWorkers < 6 && remaining.length > 0) {
            const nextIdx = remaining.shift();
            activeWorkers++;
            loadFrame(nextIdx, () => {
              activeWorkers--;
              pumpQueue();
            });
          }
        };
        setTimeout(pumpQueue, 100);
      }
    };
  }

  dismissPreloader() {
    if (!this.preloader || this.preloader.classList.contains('loaded')) return;
    if (this.loaderBar) this.loaderBar.style.width = '100%';
    if (this.loaderPercent) this.loaderPercent.textContent = '100%';
    this.preloader.classList.add('loaded');
    this.drawFrame(Math.round(this.currentFrame));
  }

  handleScroll() {
    if (!this.container) return;

    if (!this.cachedMaxScroll || this.cachedMaxScroll <= 0) {
      this.updateLayoutMetrics();
    }
    const maxScroll = this.cachedMaxScroll;
    if (maxScroll <= 0) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    const containerTop = this.cachedContainerTop;

    let progress = (scrollTop - containerTop) / maxScroll;
    progress = Math.max(0, Math.min(1, progress));

    this.targetFrame = progress * (this.totalFrames - 1);

    // On mobile touch devices: zero lag, instantaneous 1:1 frame drawing with thumb movement
    if (this.isMobile) {
      this.currentFrame = this.targetFrame;
      this.drawFrame(Math.round(this.targetFrame));
    }

    // Update Bottom Progress Bar
    if (this.bottomProgressFill) {
      this.bottomProgressFill.style.width = `${(progress * 100).toFixed(1)}%`;
    }

    // Keep SCROLL word visible throughout animation; only fade out at the very end of the hero sequence (98%+)
    if (this.bottomScrollWrapper) {
      if (progress >= 0.98) {
        this.bottomScrollWrapper.classList.add('finished');
      } else {
        this.bottomScrollWrapper.classList.remove('finished');
      }
    }

    // Smoothly fade coming soon hero overlay as user scrolls into canvas frames
    if (this.comingSoonHero) {
      if (progress < 0.04) {
        this.comingSoonHero.style.opacity = '1';
        this.comingSoonHero.style.transform = 'translate(-50%, -50%) scale(1)';
        this.comingSoonHero.style.pointerEvents = 'auto';
      } else if (progress < 0.22) {
        const fade = (0.22 - progress) / (0.22 - 0.04);
        this.comingSoonHero.style.opacity = Math.max(0, fade).toFixed(3);
        this.comingSoonHero.style.transform = `translate(-50%, calc(-50% - ${(1 - fade) * 35}px)) scale(${0.96 + 0.04 * fade})`;
        this.comingSoonHero.style.pointerEvents = fade < 0.1 ? 'none' : 'auto';
      } else {
        this.comingSoonHero.style.opacity = '0';
        this.comingSoonHero.style.pointerEvents = 'none';
      }
    }

    this.updateNarrativeOverlays(progress);
  }

  updateNarrativeOverlays(p) {
    if (p < 0.16) {
      this.setActiveOverlay('hero');
    } else if (p < 0.40) {
      this.setActiveOverlay('deconstruction');
    } else if (p < 0.65) {
      this.setActiveOverlay('blueprint');
    } else if (p < 0.85) {
      this.setActiveOverlay('cyberpunk');
    } else {
      this.setActiveOverlay('dropHud');
    }
  }

  setActiveOverlay(activeKey) {
    if (this.currentActiveOverlay === activeKey) return;
    this.currentActiveOverlay = activeKey;

    Object.keys(this.overlays).forEach(key => {
      const el = this.overlays[key];
      if (el) {
        if (key === activeKey) {
          el.classList.add('active');
        } else {
          el.classList.remove('active');
        }
      }
    });
  }

  drawFrame(frameIndex) {
    if (!this.ctx || !this.canvas) return;

    frameIndex = Math.max(0, Math.min(this.totalFrames - 1, frameIndex));
    let img = this.images[frameIndex];

    // If target frame not loaded yet, find closest loaded frame
    if (!img || !img.complete || img.naturalWidth === 0) {
      let nearest = null;
      let minDiff = Infinity;
      for (let i = 0; i < this.totalFrames; i++) {
        const candidate = this.images[i];
        if (candidate && candidate.complete && candidate.naturalWidth > 0) {
          const diff = Math.abs(i - frameIndex);
          if (diff < minDiff) {
            minDiff = diff;
            nearest = candidate;
          }
        }
      }
      if (!nearest) return;
      img = nearest;
    }

    this.renderImageResponsive(img);
    this.lastRenderedFrame = frameIndex;
    this.lastRenderedImage = img;

    if (!this.hasRenderedFirstFrame) {
      this.hasRenderedFirstFrame = true;
      const poster = document.getElementById('hero-fallback-poster');
      if (poster) poster.style.opacity = '0';
    }
  }

  renderImageResponsive(img) {
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    const iw = img.naturalWidth || 1280;
    const ih = img.naturalHeight || 720;

    if (ch > cw) {
      // DEDICATED PHONE / PORTRAIT PIPELINE
      // Scale to fit width cleanly without cropping
      const scale = cw / iw;
      const renderW = cw;
      const renderH = Math.round(ih * scale);
      const renderX = 0;
      const renderY = Math.round((ch - renderH) / 2);

      // Fast letterbox background fill only if needed
      this.ctx.fillStyle = '#050505';
      if (renderY > 0) {
        this.ctx.fillRect(0, 0, cw, renderY);
        this.ctx.fillRect(0, renderY + renderH, cw, ch - (renderY + renderH));
      }

      // Single-pass direct GPU draw - instantaneous
      this.ctx.drawImage(img, 0, 0, iw, ih, renderX, renderY, renderW, renderH);
    } else {
      // DEDICATED LAPTOP / DESKTOP WIDESCREEN PIPELINE
      this.ctx.fillStyle = '#050505';
      this.ctx.fillRect(0, 0, cw, ch);

      const hRatio = cw / iw;
      const vRatio = ch / ih;
      const ratio = Math.min(hRatio, vRatio);

      const renderW = Math.round(iw * ratio);
      const renderH = Math.round(ih * ratio);
      const renderX = Math.round((cw - renderW) / 2);
      const renderY = Math.round((ch - renderH) / 2);

      this.ctx.drawImage(img, 0, 0, iw, ih, renderX, renderY, renderW, renderH);
    }
  }

  renderLoop() {
    if (!this.isMobile) {
      const diff = this.targetFrame - this.currentFrame;
      if (Math.abs(diff) > 0.002) {
        this.currentFrame += diff * 0.22;
        this.drawFrame(Math.round(this.currentFrame));
      } else {
        const rounded = Math.round(this.targetFrame);
        if (this.lastRenderedFrame !== rounded || (this.images[rounded]?.complete && this.lastRenderedImage !== this.images[rounded])) {
          this.currentFrame = rounded;
          this.drawFrame(rounded);
        }
      }
    } else {
      // On mobile, if targetFrame changed during momentum scroll, ensure latest frame is painted
      const rounded = Math.round(this.targetFrame);
      if (this.lastRenderedFrame !== rounded) {
        this.currentFrame = this.targetFrame;
        this.drawFrame(rounded);
      }
    }

    requestAnimationFrame(() => this.renderLoop());
  }
}

window.ScrollyCanvasEngine = ScrollyCanvasEngine;
